import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Refrigerator, CalendarDays, Search as SearchIcon, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { SearchBar } from '@/components/ui/SearchBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { useCookbookStore } from '@/stores/cookbook-store';
import { usePantryStore } from '@/stores/pantry-store';
import { useMealPlanStore } from '@/stores/meal-plan-store';
import { addDays } from '@/components/pantry/pantry-utils';
import type { MealType } from '@/types';

const MEAL_LABEL_KEYS: Record<MealType, string> = {
  breakfast: 'recipe:mealPlan.breakfast',
  lunch: 'recipe:mealPlan.lunch',
  dinner: 'recipe:mealPlan.dinner',
  snack: 'recipe:mealPlan.snack',
};

function getStartOfIsoWeek(dateString: string): string {
  const d = new Date(`${dateString}T00:00:00`);
  const day = d.getDay(); // 0 = Sunday
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((day + 6) % 7));
  return monday.toISOString().slice(0, 10);
}

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ResultKind = 'recipe' | 'pantry' | 'meal';

interface SearchResult {
  kind: ResultKind;
  id: string;
  title: string;
  subtitle?: string;
  onSelect: () => void;
}

export function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'pages', 'recipe', 'pantry']);

  const recipes = useCookbookStore((s) => s.recipes);
  const pantryItems = usePantryStore((s) => s.items);
  const mealEntries = useMealPlanStore((s) => s.entries);

  const [query, setQuery] = React.useState('');
  const deferredQuery = React.useDeferredValue(query);
  const normalized = deferredQuery.trim().toLowerCase();

  // Reset input when dialog is closed
  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);

  const recipeMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const r of recipes) map.set(r.id, r.name);
    return map;
  }, [recipes]);

  const recipeResults = React.useMemo<SearchResult[]>(() => {
    if (!normalized) return [];
    return recipes
      .filter((recipe) => {
        return (
          recipe.name.toLowerCase().includes(normalized) ||
          recipe.description.toLowerCase().includes(normalized) ||
          recipe.tags.some((tag) => tag.toLowerCase().includes(normalized)) ||
          recipe.cuisine.toLowerCase().includes(normalized)
        );
      })
      .slice(0, 6)
      .map((recipe) => ({
        kind: 'recipe' as const,
        id: recipe.id,
        title: recipe.name,
        subtitle: recipe.cuisine ? t(`recipe:cuisine.${recipe.cuisine}`) : undefined,
        onSelect: () => {
          close();
          navigate(`/cookbook/${recipe.id}`);
        },
      }));
  }, [recipes, normalized, navigate, close, t]);

  const pantryResults = React.useMemo<SearchResult[]>(() => {
    if (!normalized) return [];
    return pantryItems
      .filter((item) => item.ingredientName.toLowerCase().includes(normalized))
      .slice(0, 6)
      .map((item) => ({
        kind: 'pantry' as const,
        id: item.id,
        title: item.ingredientName,
        subtitle: item.amount > 0
          ? `${item.amount} ${item.unit}${item.location ? ' · ' + item.location : ''}`
          : t('pantry:item.outOfStock'),
        onSelect: () => {
          close();
          navigate('/pantry');
        },
      }));
  }, [pantryItems, normalized, navigate, close, t]);

  const mealResults = React.useMemo<SearchResult[]>(() => {
    if (!normalized) return [];
    const today = new Date().toISOString().slice(0, 10);
    const weekStart = getStartOfIsoWeek(today);
    const weekEnd = addDays(weekStart, 6);
    return mealEntries
      .filter((entry) => {
        if (entry.date < weekStart || entry.date > weekEnd) return false;
        const name = entry.recipeId ? recipeMap.get(entry.recipeId) : entry.customMealName;
        return name ? name.toLowerCase().includes(normalized) : false;
      })
      .slice(0, 6)
      .map((entry) => {
        const name = entry.recipeId ? recipeMap.get(entry.recipeId) ?? '' : entry.customMealName ?? '';
        const slotLabel = t(MEAL_LABEL_KEYS[entry.mealType] as never) as string;
        return {
          kind: 'meal' as const,
          id: entry.id,
          title: name,
          subtitle: `${slotLabel} · ${entry.date}`,
          onSelect: () => {
            close();
            navigate('/planner');
          },
        };
      });
  }, [mealEntries, normalized, recipeMap, navigate, close, t]);

  const allResults = React.useMemo(
    () => [...recipeResults, ...pantryResults, ...mealResults],
    [recipeResults, pantryResults, mealResults]
  );

  const hasResults = allResults.length > 0;

  // Keyboard: Enter selects the first result
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const first = allResults[0];
    if (event.key === 'Enter' && first) {
      event.preventDefault();
      first.onSelect();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t('common:search.globalTitle')}
        description={t('common:search.globalDesc')}
        size="lg"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <SearchBar
            value={query}
            onChange={setQuery}
            onKeyDown={handleKeyDown}
            placeholder={t('common:search.globalPlaceholder')}
            autoFocus
          />

          {!normalized && (
            <div
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: 'var(--text-tertiary)',
                fontSize: '14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <SearchIcon style={{ width: '28px', height: '28px', color: 'var(--text-muted)' }} />
              <span>{t('common:search.globalHint')}</span>
            </div>
          )}

          {normalized && !hasResults && (
            <EmptyState
              type="no-results"
              action={{ label: t('common:btn.close'), onClick: close }}
            />
          )}

          {hasResults && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                maxHeight: '60vh',
                overflowY: 'auto',
              }}
            >
              {recipeResults.length > 0 && (
                <ResultGroup
                  icon={<BookOpen style={{ width: '14px', height: '14px' }} />}
                  label={t('common:search.groupRecipes', { count: recipeResults.length })}
                  results={recipeResults}
                />
              )}
              {pantryResults.length > 0 && (
                <ResultGroup
                  icon={<Refrigerator style={{ width: '14px', height: '14px' }} />}
                  label={t('common:search.groupPantry', { count: pantryResults.length })}
                  results={pantryResults}
                />
              )}
              {mealResults.length > 0 && (
                <ResultGroup
                  icon={<CalendarDays style={{ width: '14px', height: '14px' }} />}
                  label={t('common:search.groupMeals', { count: mealResults.length })}
                  results={mealResults}
                />
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ResultGroupProps {
  icon: React.ReactNode;
  label: string;
  results: SearchResult[];
}

function ResultGroup({ icon, label, results }: ResultGroupProps) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-tertiary)',
          marginBottom: '6px',
        }}
      >
        {icon}
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {results.map((result) => (
          <button
            key={`${result.kind}:${result.id}`}
            onClick={result.onSelect}
            className="ui-focusable"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-primary)',
              background: 'var(--bg-primary)',
              cursor: 'pointer',
              textAlign: 'start',
              transition: 'background var(--motion-duration-fast) var(--motion-ease-standard), transform var(--motion-duration-fast) var(--motion-ease-emphasis)',
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.background = 'var(--accent-primary-bg)';
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.background = 'var(--bg-primary)';
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {result.title}
              </div>
              {result.subtitle && (
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {result.subtitle}
                </div>
              )}
            </div>
            <ArrowRight className="rtl-mirror" style={{ width: '16px', height: '16px', color: 'var(--text-muted)', flexShrink: 0 }} />
          </button>
        ))}
      </div>
    </div>
  );
}
